import { resetMultiDimFilter, setFilterItems, setMultiDimFilter } from 'redux/PlanPageSlice';
import { getTableFilterPayload, hasActiveDimensionFilters } from 'utils/GlobalHelpers';

import type { AppDispatch } from 'utils/GlobalHelpers';

type DimensionFilterEntry = Record<string, string[]>;

interface BlockDimensionMeta {
  id?: number | string;
  name: string;
  items?: string[];
}

interface SyncPlanFiltersToInputPanelOptions {
  /** Applied plan-page Filters (not draft checkbox state). */
  appliedFilters: DimensionFilterEntry[] | null | undefined;
  blockDimensions: { dimensions?: BlockDimensionMeta[] } | null | undefined;
  /** Dimension columns on the indicator ``data_input`` payload (direct dims only). */
  indicatorDimensions: Array<{ id: number | string }> | null | undefined;
}

/**
 * Mirrors active plan-page dimension filters into the indicator input-panel grid
 * via ``multiDimFilters`` (client-side row filtering in MultiDimensionTableOpzV2).
 *
 * Replaces any previous plan-driven filters on each call so apply/clear/update
 * on the plan toolbar always reflects in the open input panel.
 *
 * Only block dimensions that exist as direct columns on the indicator ``data_input``
 * are synced. Connected / linked org dimensions are not supported here.
 */
export const syncPlanFiltersToIndicatorInputPanel = (
  dispatch: AppDispatch,
  { appliedFilters, blockDimensions, indicatorDimensions }: SyncPlanFiltersToInputPanelOptions,
): boolean => {
  if (!indicatorDimensions?.length || !blockDimensions?.dimensions?.length) {
    return false;
  }

  if (!hasActiveDimensionFilters(appliedFilters, blockDimensions)) {
    dispatch(resetMultiDimFilter());
    return false;
  }

  dispatch(resetMultiDimFilter());

  const activeIndicatorDimIds = new Set(indicatorDimensions.map((dim) => String(dim.id)));
  let synced = false;

  (appliedFilters ?? []).forEach((selection) => {
    const [dimName, selectedValues] = Object.entries(selection ?? {})[0] ?? [];
    if (!dimName || !Array.isArray(selectedValues) || selectedValues.length === 0) {
      return;
    }

    const dimensionMeta = blockDimensions.dimensions?.find((dim) => dim.name === dimName);
    if (!dimensionMeta || !activeIndicatorDimIds.has(String(dimensionMeta.id))) {
      return;
    }

    const totalItems = dimensionMeta.items?.length ?? 0;
    if (totalItems === 0 || selectedValues.length >= totalItems) {
      return;
    }

    const payload = getTableFilterPayload(selection, dimensionMeta);
    dispatch(setFilterItems(payload.dimensionItems));
    dispatch(setMultiDimFilter(payload.tableItems));
    synced = true;
  });

  return synced;
};

/** Clears input-panel dimension row filters driven from the plan-page filter bar. */
export const clearPlanFiltersFromIndicatorInputPanel = (dispatch: AppDispatch) => {
  dispatch(resetMultiDimFilter());
};
